import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { stopImpersonation, getImpersonationData } from '../../lib/auth/impersonationService';
import toast from 'react-hot-toast';

const ImpersonationBanner: React.FC = () => {
  const [isExiting, setIsExiting] = useState(false);
  const navigate = useNavigate();
  const impersonationData = getImpersonationData();

  if (!impersonationData.isImpersonating) {
    return null;
  }

  const handleExitImpersonation = async () => {
    if (isExiting) return;

    setIsExiting(true);
    try {
      const { success, error } = await stopImpersonation();
      
      if (success) {
        toast.success('Exited impersonation mode');
        // Redirect to login page after exiting impersonation
        window.location.href = '/login';
      } else {
        toast.error(`Failed to exit impersonation: ${error}`);
        setIsExiting(false);
      }
    } catch (error) {
      console.error('Error exiting impersonation:', error);
      toast.error('Failed to exit impersonation');
      setIsExiting(false);
    }
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <svg 
                className="w-6 h-6 animate-pulse" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
                />
              </svg>
              <span className="font-bold text-lg">IMPERSONATION MODE</span>
            </div>
            <div className="hidden sm:block h-6 w-px bg-white/30"></div>
            <div className="hidden sm:flex flex-col text-sm">
              <span className="font-semibold">
                Viewing as: {impersonationData.targetFirstName} {impersonationData.targetLastName}
              </span>
              <span className="text-xs opacity-90">
                {impersonationData.targetEmail}
              </span>
            </div>
          </div>
          
          <button
            onClick={handleExitImpersonation}
            disabled={isExiting}
            className="flex items-center gap-2 bg-white text-red-600 px-4 py-2 rounded-lg font-semibold hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
          >
            {isExiting ? (
              <>
                <svg 
                  className="w-5 h-5 animate-spin" 
                  fill="none" 
                  viewBox="0 0 24 24"
                >
                  <circle 
                    className="opacity-25" 
                    cx="12" 
                    cy="12" 
                    r="10" 
                    stroke="currentColor" 
                    strokeWidth="4"
                  />
                  <path 
                    className="opacity-75" 
                    fill="currentColor" 
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span>Exiting...</span>
              </>
            ) : (
              <>
                <svg 
                  className="w-5 h-5" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" 
                  />
                </svg>
                <span>Exit Impersonation</span>
              </>
            )}
          </button>
        </div>
        
        {/* Mobile view - show user info below on small screens */}
        <div className="sm:hidden mt-2 text-sm border-t border-white/20 pt-2">
          <div className="font-semibold">
            Viewing as: {impersonationData.targetFirstName} {impersonationData.targetLastName}
          </div>
          <div className="text-xs opacity-90">
            {impersonationData.targetEmail}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImpersonationBanner;